import { createContext, useContext, useEffect, useState } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { identifyUser, resetUser, trackEvent } from "@/lib/posthog";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
  role: string | null;
  permissions: Record<string, boolean>;
  hasPermission: (permission: string) => boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  signOut: async () => { },
  role: null,
  permissions: {},
  hasPermission: () => false,
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<Record<string, boolean>>({});

  const fetchProfile = async (userId: string, userEmail?: string) => {
    try {
      // First try by user_id in the profiles table
      let { data, error } = await supabase
        .from('profiles')
        .select('id, admin_role, permissions, user_id')
        .eq('user_id', userId)
        .maybeSingle();

      // If not found by user_id, try by email
      if (!data && userEmail) {
        const { data: emailData, error: emailError } = await supabase
          .from('profiles')
          .select('id, admin_role, permissions, user_id')
          .eq('email', userEmail)
          .maybeSingle();

        if (emailData && !emailError) {
          data = emailData;
          // Link the user_id for future fetches
          await supabase
            .from('profiles')
            .update({ user_id: userId } as any)
            .eq('id', (emailData as any).id);
        }
      }

      if (data) {
        setRole((data as any).admin_role);
        setPermissions((data as any).permissions as Record<string, boolean> || {});
      }
    } catch (err) {
      console.error('Error fetching admin profile:', err);
    }
  };

  const hasPermission = (permission: string) => {
    if (role === 'super_admin') return true;
    return !!permissions[permission];
  };

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          fetchProfile(session.user.id, session.user.email);
        } else {
          setRole(null);
          setPermissions({});
        }

        setLoading(false);

        // Track auth events in PostHog
        if (event === 'SIGNED_IN' && session?.user) {
          identifyUser(session.user.id, {
            email: session.user.email,
            created_at: session.user.created_at,
          });
          trackEvent('user_signed_in', {
            method: 'email',
          });
        } else if (event === 'SIGNED_OUT') {
          trackEvent('user_signed_out');
          resetUser();
        }
      }
    );

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);

      if (session?.user) {
        fetchProfile(session.user.id);
      } else {
        setRole(null);
        setPermissions({});
      }

      setLoading(false);

      // Identify user if already logged in
      if (session?.user) {
        identifyUser(session.user.id, {
          email: session.user.email,
          created_at: session.user.created_at,
        });
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const value = {
    user,
    session,
    loading,
    signOut,
    role,
    permissions,
    hasPermission,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};